import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthStateService } from '@guards/auth-state.service';
import { CartStateService } from '@services/cartStateService';
import { AppService } from '@services/app.service';
import { CategoryDetails } from '@/models/category-details/category-details';
import { ConfigService } from '@services/config.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-web-navbar',
  templateUrl: './web-navbar.component.html',
  styleUrl: './web-navbar.component.scss'
})
export class WebNavbarComponent implements OnInit, OnDestroy {
  @Input() hideCategoryBar: boolean = false;
  userId: number = 0;
  userType: string = "";
  user: any = null;
  cartCount: number = 0;
  openedCategory: any = null;
  categoriesOpen: boolean = false;
  megaMenuHidden: boolean = false;

  // Search
  searchQuery: string = '';
  searchResults: any[] = [];
  showSearchResults: boolean = false;
  isSearching: boolean = false;
  searchOpen: boolean = false;
  private searchSubject = new Subject<string>();
  private endpoint: string = '';



  // API-fetched categories
  categoryDetails: CategoryDetails[] = [];
  visibleCategories: CategoryDetails[] = [];
  moreCategories: CategoryDetails[] = [];

  @ViewChild('mobileMenu', { static: false }) mobileMenuRef!: ElementRef;
  @ViewChild('searchInput') searchInputRef!: ElementRef;
  private subscriptions = new Subscription();

  constructor(
    private authState: AuthStateService,
    private cartState: CartStateService,
    private appService: AppService,
    private router: Router,
    private config: ConfigService
  ) { }

  ngOnInit(): void {
    this.endpoint = this.config.getConfig().END_POINT;

    // ✅ Listen Logged-in User & load cart count from API
    this.subscriptions.add(
      this.authState.user$.subscribe(user => {
        this.user = user;
        if (user) {
          this.loadCartCount();
        } else {
          this.cartState.clearCart();
        }
      })
    );

    // ✅ Listen Cart Count
    this.subscriptions.add(
      this.cartState.cartCount$.subscribe(count => {
        this.cartCount = count;
      })
    );

    // ✅ Fetch categories from API
    this.subscriptions.add(
      this.appService.getCategoryDetailsByActive().subscribe(data => {
        this.categoryDetails = data;
        this.visibleCategories = data.slice(0, 7);
        this.moreCategories = data.slice(7);
      })
    );

    // ✅ Search debounce setup
    this.subscriptions.add(
      this.searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(keyword => {
          if (!keyword || keyword.trim().length < 2) {
            this.searchResults = [];
            this.showSearchResults = false;
            this.isSearching = false;
            return [];
          }
          this.isSearching = true;
          return this.appService.searchProducts(keyword.trim());
        })
      ).subscribe({
        next: (results: any[]) => {
          this.searchResults = (results || []).slice(0, 8);
          this.showSearchResults = this.searchResults.length > 0;
          this.isSearching = false;
        },
        error: () => {
          this.searchResults = [];
          this.showSearchResults = false;
          this.isSearching = false;
        }
      })
    );
  }

  /** Fetch actual cart from API and sync the badge count */
  private loadCartCount(): void {
    this.subscriptions.add(
      this.appService.getByCustomerCartDetails().subscribe({
        next: (res: any[]) => {
          const cart = res?.[0];
          if (cart?.cartItems?.length) {
            const totalQty = cart.cartItems.reduce(
              (sum: number, ci: any) => sum + (ci.quantity || 0), 0
            );
            this.cartState.setCount(totalQty);
          }
        },
        error: () => { /* silently ignore – user may not have a cart yet */ }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // ===== SEARCH =====
  onSearchInput(event: any): void {
    const value = event.target.value;
    this.searchQuery = value;
    this.searchSubject.next(value);
  }

  toggleSearch(): void {
    this.searchOpen = !this.searchOpen;
    if (this.searchOpen) {
      setTimeout(() => this.searchInputRef?.nativeElement?.focus(), 100);
    } else {
      this.closeSearch();
    }
  }

  closeSearch(): void {
    this.searchOpen = false;
    this.searchQuery = '';
    this.searchResults = [];
    this.showSearchResults = false;
  }

  navigateToProduct(product: any): void {
    if (!product?.productId) return;
    const encodedProduct = btoa(JSON.stringify(product.productId));
    const queryParams: any = { product: encodedProduct };

    // Find matching variant by barcode and pass variantId
    const matchedVariant = this.findMatchingVariant(product);
    if (matchedVariant) {
      queryParams.variantId = matchedVariant.productVariantId;
    }

    this.router.navigate(['/detail-view'], { queryParams });
    this.closeSearch();
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  }

  /**
   * Find the variant whose barcodeNumber matches the current search query.
   * Returns the matched variant or null.
   */
  private findMatchingVariant(product: any): any {
    if (!this.searchQuery || !Array.isArray(product.variants) || product.variants.length === 0) {
      return null;
    }
    const query = this.searchQuery.trim().toLowerCase();

    // Exact barcode match first
    const exactMatch = product.variants.find(
      (v: any) => v.barcodeNumber && v.barcodeNumber.toLowerCase() === query
    );
    if (exactMatch) return exactMatch;

    // Partial barcode match (search query is contained in barcode)
    const partialMatch = product.variants.find(
      (v: any) => v.barcodeNumber && v.barcodeNumber.toLowerCase().includes(query)
    );
    if (partialMatch) return partialMatch;

    return null;
  }

  /**
   * Get the image for the variant matching the search query barcode.
   * Falls back to first variant image if no barcode match.
   */
  private getVariantImage(variant: any): string | null {
    if (!variant || !Array.isArray(variant.images)) return null;
    const activeImages = variant.images.filter((img: any) => img.isActive);
    const primary = activeImages.find((img: any) => img.isPrimary);
    const img = primary || activeImages[0];
    if (img?.imageDetailsId) {
      return `${this.endpoint}/api/product/image-details/get/${img.imageDetailsId}`;
    }
    return null;
  }

  getSearchImageUrl(product: any): string {
    const defaultImage = 'assets/img/my-1.jpg';

    // 1. Try to find variant matching the barcode search query
    const matchedVariant = this.findMatchingVariant(product);
    if (matchedVariant) {
      const matchedImage = this.getVariantImage(matchedVariant);
      if (matchedImage) return matchedImage;
    }

    // 2. Direct image ID properties (API results often prefix with 'product')
    const directImageId = product.imageId ||
      product.imageDetailsId ||
      product.productImageId ||
      product.productImageDetailsId ||
      product.variants?.[0]?.imageId ||
      product.variants?.[0]?.imageDetailsId;

    if (directImageId) {
      return `${this.endpoint}/api/product/image-details/get/${directImageId}`;
    }

    // 3. Direct images array
    if (Array.isArray(product.images) && product.images.length > 0) {
      const primaryImg = product.images.find((img: any) => img.isPrimary) || product.images[0];
      if (primaryImg?.imageDetailsId) {
        return `${this.endpoint}/api/product/image-details/get/${primaryImg.imageDetailsId}`;
      }
    }

    // 4. Fallback to variants (standard product structure)
    if (Array.isArray(product.variants) && product.variants.length > 0) {
      for (const variant of product.variants) {
        if (!Array.isArray(variant.images)) continue;
        const primaryImage = variant.images.find((img: any) => img.isPrimary === true && img.isActive === true);
        if (primaryImage?.imageDetailsId) {
          return `${this.endpoint}/api/product/image-details/get/${primaryImage.imageDetailsId}`;
        }
      }

      for (const variant of product.variants) {
        if (!Array.isArray(variant.images)) continue;
        const firstActive = variant.images.find((img: any) => img.isActive === true);
        if (firstActive?.imageDetailsId) {
          return `${this.endpoint}/api/product/image-details/get/${firstActive.imageDetailsId}`;
        }
      }
    }

    return defaultImage;
  }

  getProductPrice(product: any): number {
    // 1. Try to get price from barcode-matched variant
    const matchedVariant = this.findMatchingVariant(product);
    if (matchedVariant) {
      return matchedVariant.productSrp || matchedVariant.srp || matchedVariant.productMrp || matchedVariant.mrp || 0;
    }

    // 2. Direct price property
    if (product.productSrp) return product.productSrp;
    if (product.srp) return product.srp;
    if (product.productMrp) return product.productMrp;
    if (product.price) return product.price;

    // 3. Fallback to variants
    if (product.variants && product.variants.length > 0) {
      const v = product.variants[0];
      return v?.productSrp || v?.srp || v?.productMrp || v?.mrp || 0;
    }

    return 0;
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/img/my-1.jpg';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.search-wrapper') && !target.closest('.mobile-search-wrapper')) {
      this.showSearchResults = false;
    }
  }

  // ✅ Navigate to home with category filter
  navigateToCategory(categoryId: number) {
    this.router.navigate(['/'], { queryParams: { categoryId } });
  }

  // ✅ Navigate to home with subcategory filter
  navigateToSubCategory(categoryId: number, subCategoryId: number) {
    this.router.navigate(['/'], { queryParams: { categoryId, subCategoryId } });
  }

  // ✅ Logout
  logout() {
    this.authState.clearUser();
    this.cartState.clearCart();
    this.router.navigate(['/customer-login']);
  }

  toggleCategory(category: any) {
    if (this.openedCategory === category) {
      this.openedCategory = null;
    } else {
      this.openedCategory = category;
    }
  }

  handleMobileCategoryClick(cat: any) {
    if (cat.subCategories && cat.subCategories.length > 0) {
      this.toggleCategory(cat);
    } else {
      this.navigateToCategory(cat.categoryId);
      this.closeOffcanvas();
    }
  }

  closeOffcanvas() {
    if (this.mobileMenuRef?.nativeElement) {
      const bsOffcanvas = (window as any).bootstrap.Offcanvas.getInstance(this.mobileMenuRef.nativeElement);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      }
    }
  }

}